import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMeQuery, useGroupsQuery } from "@/services/user";
import { getUserFromLocalStorage } from "@/utils/getUserFromLocalStorage";
import { inferModuleFromPath } from "@/config/routes/path-to-group";
import { LoadingSpinner } from "@/components/loading/LoadingSpinner";
import type { User, GroupItens, SubstitutionInfo } from "@/@types/user";
import type { GroupsEnum } from "@/enum/groups";

interface AuthDataContextType {
  user: User | null;
  groups: GroupItens[] | null;
  groupNames: string[];
  substitution: SubstitutionInfo | null;
  currentModule: GroupsEnum | null;
  isLoading: boolean;
  isRefreshing: boolean;
}

const AuthDataContext = createContext<AuthDataContextType | null>(null);

export function AuthDataProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();

  const {
    data: userData,
    isLoading: userLoading,
    isFetching: userFetching,
  } = useMeQuery(undefined, { refetchOnFocus: true });

  const {
    data: groupsData,
    isLoading: groupsLoading,
    isFetching: groupsFetching,
  } = useGroupsQuery();

  const cachedUser = useMemo(() => getUserFromLocalStorage(), []);
  const cachedGroupNames = useMemo<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem("@azure:groups") || "[]");
    } catch {
      return [];
    }
  }, []);

  const user = userData ?? cachedUser;
  const groups = (groupsData as unknown as GroupItens[] | undefined) ?? null;
  const substitution = userData?.substitution ?? null;

  const groupNames = useMemo(() => {
    const perms = user?.user_permission_group;
    return Array.isArray(perms) && perms.length > 0
      ? perms.map((g) => g.group)
      : cachedGroupNames;
  }, [user, cachedGroupNames]);

  const currentModule = useMemo(
    () => inferModuleFromPath(location.pathname),
    [location.pathname]
  );

  useEffect(() => {
    if (userData) {
      localStorage.setItem("@azure:user", JSON.stringify(userData));
    }
  }, [userData]);

  useEffect(() => {
    if (groups && Array.isArray(groups)) {
      const names = groups.map((g) => g.name);
      localStorage.setItem("@azure:groups", JSON.stringify(names));
    }
  }, [groups]);

  useEffect(() => {
    if (currentModule) {
      localStorage.setItem("@verzel:module", currentModule);
    }
  }, [currentModule]);

  useEffect(() => {
    if (!currentModule) return;
    if (!user) return;
    if (groupNames.length === 0) return;

    if (!groupNames.includes(currentModule)) {
      navigate("/", { replace: true });
    }
  }, [currentModule, user, groupNames, navigate]);

  const isInitialLoad =
    (userLoading && !cachedUser) || (groupsLoading && cachedGroupNames.length === 0);

  if (isInitialLoad) {
    return <LoadingSpinner />;
  }

  return (
    <AuthDataContext.Provider
      value={{
        user,
        groups,
        groupNames,
        substitution,
        currentModule,
        isLoading: isInitialLoad,
        isRefreshing: userFetching || groupsFetching,
      }}
    >
      {children}
    </AuthDataContext.Provider>
  );
}

export function useAuthData(): AuthDataContextType | null {
  return useContext(AuthDataContext);
}
